import React, { useEffect, useState } from 'react';
import {
  Layout, Card, Button, Table, Modal, Form, Input, InputNumber, Select,
  Tag, Space, Typography, message, Row, Col, Tooltip, Popconfirm, DatePicker, Tabs, Alert, Upload, Progress
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CalendarOutlined,
  ReloadOutlined, UserOutlined, TeamOutlined, UploadOutlined, DownloadOutlined,
  FileExcelOutlined, FileTextOutlined, InboxOutlined, LineChartOutlined, RiseOutlined,
  AimOutlined, SlidersOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import Sidebar from './Sidebar';
import MainHeader from './MainHeader';
import api from '../api';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const PRIORITY_COLOR_MAP = {
  Critical: 'red',
  High: 'orange',
  Medium: 'blue',
  Low: 'default',
};

const STATUS_COLOR_MAP = {
  'Not Started': 'default',
  'In Progress': 'processing',
  Completed: 'success',
  Achieved: 'success',
  Exceeded: 'purple',
  'Not Achieved': 'error',
  Cancelled: 'error',
};

export default function PmsGoalSetting() {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [goals, setGoals] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);

  const [editingGoal, setEditingGoal] = useState(null);
  const [progressGoal, setProgressGoal] = useState(null);

  const [form] = Form.useForm();
  const [templateForm] = Form.useForm();
  const [progressForm] = Form.useForm();

  // Bulk Upload State
  const [bulkRawText, setBulkRawText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [activeUploadTab, setActiveUploadTab] = useState('excel');

  // Filters
  const [filterUser, setFilterUser] = useState(null);
  const [filterPriority, setFilterPriority] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/pms/goals');
      if (res.data?.success) {
        setGoals(res.data.goals || []);
      } else {
        message.error(res.data?.message || 'Failed to load goals');
      }
    } catch (e) {
      console.error('Fetch goals error:', e);
      message.error(e.response?.data?.message || 'Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuxiliaryData = async () => {
    try {
      // Staff list
      const staffRes = await api.get('/admin/staff');
      const sData = staffRes?.data?.staff || staffRes?.data?.data || [];
      setStaffList(sData);

      // Cycles
      const cyclesRes = await api.get('/admin/pms/cycles');
      if (cyclesRes.data?.success) setCycles(cyclesRes.data.cycles || []);

      // KPIs
      const kpisRes = await api.get('/admin/pms/kpis');
      if (kpisRes.data?.success) setKpis(kpisRes.data.kpis || []);

      // Dynamic Departments
      const bfResp = await api.get('/admin/business-functions');
      const bfList = bfResp?.data?.data || [];
      const deptFn = bfList.find((f) => String(f.name || '').toLowerCase() === 'department');
      const deptValues = (deptFn?.values || []).map(v => v.value).filter(Boolean);
      setDepartments(deptValues);
    } catch (e) {
      console.error('Fetch auxiliary data error:', e);
    }
  };

  useEffect(() => {
    fetchGoals();
    fetchAuxiliaryData();
  }, []);

  // Modal Handlers
  const handleOpenModal = (goalObj = null) => {
    setEditingGoal(goalObj);
    if (goalObj) {
      const targetVal = parseFloat(String(goalObj.target || '').replace(/,/g, '')) || 0;
      const achieveVal = parseFloat(String(goalObj.achievement || '').replace(/,/g, '')) || 0;
      let pct = goalObj.achievementPct !== undefined && goalObj.achievementPct !== null ? Number(goalObj.achievementPct) : 0;
      if (!pct && targetVal > 0 && achieveVal > 0) {
        pct = Math.min(100, Math.round((achieveVal / targetVal) * 100));
      }

      form.setFieldsValue({
        userId: goalObj.userId,
        goal: goalObj.goal,
        description: goalObj.description || '',
        cycleId: goalObj.cycleId || undefined,
        kpiId: goalObj.kpiId || undefined,
        target: goalObj.target || '',
        achievement: goalObj.achievement || '',
        achievementPct: pct,
        unit: goalObj.unit || '',
        startDate: goalObj.startDate ? dayjs(goalObj.startDate) : null,
        endDate: goalObj.endDate ? dayjs(goalObj.endDate) : null,
        weightage: goalObj.weightage !== undefined ? Number(goalObj.weightage) : 0,
        priority: goalObj.priority || 'Medium',
        status: goalObj.status || 'In Progress',
        employeeComment: goalObj.employeeComment || '',
        managerComment: goalObj.managerComment || '',
        supportingDocument: goalObj.supportingDocument || '',
        active: goalObj.active !== undefined ? goalObj.active : true,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        userId: staffList.length > 0 ? staffList[0].id : undefined,
        priority: 'Medium',
        status: 'In Progress',
        weightage: 20,
        unit: '₹',
        achievementPct: 0,
        active: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingGoal(null);
    form.resetFields();
  };

  // Section 9: Goal Progress Modal Handlers
  const handleOpenProgressModal = (goalObj) => {
    setProgressGoal(goalObj);
    const targetVal = parseFloat(String(goalObj.target || '').replace(/,/g, '')) || 0;
    const achieveVal = parseFloat(String(goalObj.achievement || '').replace(/,/g, '')) || 0;
    let pct = goalObj.achievementPct !== undefined && goalObj.achievementPct !== null ? Number(goalObj.achievementPct) : 0;
    if (!pct && targetVal > 0 && achieveVal > 0) {
      pct = Math.min(100, Math.round((achieveVal / targetVal) * 100));
    }

    progressForm.setFieldsValue({
      target: goalObj.target || '',
      achievement: goalObj.achievement || '',
      achievementPct: pct,
      status: goalObj.status || 'In Progress',
      employeeComment: goalObj.employeeComment || '',
      managerComment: goalObj.managerComment || '',
      supportingDocument: goalObj.supportingDocument || '',
    });

    setIsProgressModalOpen(true);
  };

  const handleProgressSubmit = async (values) => {
    try {
      if (!progressGoal) return;

      const payload = {
        achievement: values.achievement !== undefined && values.achievement !== null ? String(values.achievement) : null,
        achievementPct: values.achievementPct !== undefined && values.achievementPct !== null ? Number(values.achievementPct) : null,
        status: values.status || 'In Progress',
        employeeComment: values.employeeComment || null,
        managerComment: values.managerComment || null,
        supportingDocument: values.supportingDocument || null,
      };

      const res = await api.put(`/admin/pms/goals/${progressGoal.id}/progress`, payload);
      if (res.data?.success) {
        message.success('Goal progress updated successfully');
        setIsProgressModalOpen(false);
        setProgressGoal(null);
        progressForm.resetFields();
        fetchGoals();
      } else {
        message.error(res.data?.message || 'Failed to update goal progress');
      }
    } catch (e) {
      console.error('Update progress error:', e);
      message.error(e.response?.data?.message || 'Failed to update goal progress');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        userId: Number(values.userId),
        goal: values.goal,
        description: values.description || null,
        cycleId: values.cycleId ? Number(values.cycleId) : null,
        kpiId: values.kpiId ? Number(values.kpiId) : null,
        target: values.target !== undefined ? String(values.target) : null,
        achievement: values.achievement !== undefined && values.achievement !== null ? String(values.achievement) : null,
        achievementPct: values.achievementPct !== undefined && values.achievementPct !== null ? Number(values.achievementPct) : null,
        unit: values.unit || null,
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : null,
        endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
        weightage: values.weightage !== undefined ? Number(values.weightage) : 0,
        priority: values.priority || 'Medium',
        status: values.status || 'In Progress',
        employeeComment: values.employeeComment || null,
        managerComment: values.managerComment || null,
        supportingDocument: values.supportingDocument || null,
        active: values.active !== undefined ? !!values.active : true,
      };

      if (editingGoal) {
        const res = await api.put(`/admin/pms/goals/${editingGoal.id}`, payload);
        if (res.data?.success) {
          message.success('Goal updated successfully');
          handleCloseModal();
          fetchGoals();
        } else {
          message.error(res.data?.message || 'Failed to update goal');
        }
      } else {
        const res = await api.post('/admin/pms/goals', payload);
        if (res.data?.success) {
          message.success('Goal assigned successfully');
          handleCloseModal();
          fetchGoals();
        } else {
          message.error(res.data?.message || 'Failed to assign goal');
        }
      }
    } catch (e) {
      console.error('Save goal error:', e);
      message.error(e.response?.data?.message || 'Failed to save goal');
    }
  };

  // Group / Template Auto Assignment Submit
  const handleTemplateSubmit = async (values) => {
    try {
      const payload = {
        department: values.department || null,
        designation: values.designation || null,
        staffType: values.staffType || null,
        goal: values.goal,
        description: values.description || null,
        target: values.target !== undefined ? String(values.target) : null,
        unit: values.unit || null,
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : null,
        endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
        weightage: values.weightage !== undefined ? Number(values.weightage) : 0,
        priority: values.priority || 'Medium',
        cycleId: values.cycleId ? Number(values.cycleId) : null,
        kpiId: values.kpiId ? Number(values.kpiId) : null,
      };

      const res = await api.post('/admin/pms/goals/template-assign', payload);
      if (res.data?.success) {
        message.success(res.data.message || 'Goals assigned to matching group successfully');
        setIsTemplateModalOpen(false);
        templateForm.resetFields();
        fetchGoals();
      } else {
        message.error(res.data?.message || 'Failed auto-assigning goals');
      }
    } catch (e) {
      console.error('Template assign error:', e);
      message.error(e.response?.data?.message || 'Failed auto-assigning goals');
    }
  };

  // Download Sample Excel Template
  const handleDownloadSampleTemplate = async () => {
    try {
      const res = await api.get('/admin/pms/goals/sample-template', {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'PMS_Goals_Import_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Sample Excel template downloaded successfully!');
    } catch (e) {
      console.error('Download sample template error:', e);
      message.error('Failed to download sample template');
    }
  };

  // Bulk Excel/CSV Upload Submit
  const handleBulkSubmit = async () => {
    try {
      setBulkLoading(true);

      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);

        const res = await api.post('/admin/pms/goals/bulk-upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (res.data?.success) {
          message.success(res.data.message || `Uploaded goals successfully!`);
          setIsBulkModalOpen(false);
          setSelectedFile(null);
          setBulkRawText('');
          fetchGoals();
        } else {
          message.error(res.data?.message || 'Failed bulk upload');
        }
      } else if (bulkRawText.trim()) {
        const lines = bulkRawText.split('\n').map(l => l.trim()).filter(Boolean);
        const rows = [];

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (i === 0 && (line.toLowerCase().includes('staff') || line.toLowerCase().includes('employee') || line.toLowerCase().includes('goal title'))) {
            continue;
          }

          const cols = line.split(/,|\t/).map(c => c.trim().replace(/^["']|["']$/g, ''));
          if (cols.length >= 2) {
            rows.push({
              staffId: cols[0],
              goal: cols[1],
              weightage: cols[2] ? Number(cols[2]) : 20,
              target: cols[3] || '',
              unit: cols[4] || '₹',
              kpi: cols[5] || '',
              cycle: cols[6] || '',
              startDate: cols[7] || null,
              endDate: cols[8] || null,
              priority: cols[9] || 'Medium',
              status: cols[10] || 'In Progress',
              description: cols[11] || '',
            });
          }
        }

        if (rows.length === 0) {
          message.error('No valid rows found in text');
          setBulkLoading(false);
          return;
        }

        const res = await api.post('/admin/pms/goals/bulk-upload', { rows });
        if (res.data?.success) {
          message.success(`Successfully uploaded ${res.data.successCount || rows.length} goals!`);
          setIsBulkModalOpen(false);
          setBulkRawText('');
          fetchGoals();
        } else {
          message.error(res.data?.message || 'Failed bulk upload');
        }
      } else {
        message.warning('Please select an Excel file (.xlsx) or enter CSV text');
      }
    } catch (e) {
      console.error('Bulk upload error:', e);
      message.error(e.response?.data?.message || 'Failed bulk upload');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/admin/pms/goals/${id}`);
      if (res.data?.success) {
        message.success('Goal deleted successfully');
        fetchGoals();
      } else {
        message.error(res.data?.message || 'Failed to delete goal');
      }
    } catch (e) {
      console.error('Delete goal error:', e);
      message.error(e.response?.data?.message || 'Failed to delete goal');
    }
  };

  // Filtered Goals
  const filteredGoals = goals.filter(g => {
    const matchesUser = !filterUser || g.userId === filterUser;
    const matchesPriority = !filterPriority || g.priority === filterPriority;
    const empName = g.employee?.profile?.name || g.employee?.name || g.employee?.phone || '';
    const matchesSearch = !searchQuery ||
      g.goal?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      empName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesUser && matchesPriority && matchesSearch;
  });

  const columns = [
    {
      title: 'Employee',
      key: 'employee',
      width: 170,
      render: (_, record) => {
        const emp = record.employee || {};
        const profile = emp.profile || {};
        const empName = profile.name || emp.name || emp.phone || `Staff #${record.userId}`;
        const desig = profile.designation || emp.designation || profile.department || emp.department || 'Employee';
        return (
          <div>
            <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '13px' }}>
              <UserOutlined style={{ marginRight: '6px', color: '#1677ff' }} />
              {empName}
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', marginLeft: '18px' }}>
              {desig}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Goal & Description',
      key: 'goal',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '13px' }}>{record.goal}</div>
          {record.description && (
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{record.description}</div>
          )}
          {record.kpi && (
            <div style={{ marginTop: '4px' }}>
              <Tag color="geekblue" style={{ fontSize: '10px' }}>
                KPI: {record.kpi.kpiName}
              </Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Target vs Achievement',
      key: 'progress',
      width: 220,
      render: (_, record) => {
        const targetVal = parseFloat(String(record.target || '').replace(/,/g, '')) || 0;
        const achieveVal = parseFloat(String(record.achievement || '').replace(/,/g, '')) || 0;
        let pct = record.achievementPct !== undefined && record.achievementPct !== null ? Number(record.achievementPct) : 0;
        if (!pct && targetVal > 0 && achieveVal > 0) {
          pct = Math.min(100, Math.round((achieveVal / targetVal) * 100));
        }

        const unitStr = record.unit || '';
        return (
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: '600' }}>
              <span style={{ color: '#64748b' }}>Target: {record.target ? `${record.target} ${unitStr}` : '-'}</span>
              <span style={{ color: '#1677ff' }}>Achieved: {record.achievement ? `${record.achievement} ${unitStr}` : '0'}</span>
            </div>
            <Progress
              percent={pct}
              size="small"
              status={pct >= 100 ? 'success' : pct > 0 ? 'active' : 'normal'}
              strokeColor={pct >= 100 ? '#52c41a' : pct >= 75 ? '#1677ff' : '#fa8c16'}
            />
          </div>
        );
      },
    },
    {
      title: 'Timeline',
      key: 'timeline',
      width: 140,
      render: (_, record) => (
        <span style={{ fontSize: '11px' }}>
          <CalendarOutlined style={{ marginRight: '4px', color: '#64748b' }} />
          {record.startDate ? dayjs(record.startDate).format('DD MMM YY') : 'N/A'} - {' '}
          {record.endDate ? dayjs(record.endDate).format('DD MMM YY') : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Weightage',
      dataIndex: 'weightage',
      key: 'weightage',
      width: 90,
      render: (val) => (
        <Tag color="blue" style={{ borderRadius: '10px', fontWeight: '700' }}>
          {Number(val || 0)}%
        </Tag>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (prio) => (
        <Tag color={PRIORITY_COLOR_MAP[prio] || 'default'} style={{ borderRadius: '10px', fontWeight: '600' }}>
          {prio || 'Medium'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={STATUS_COLOR_MAP[status] || 'default'} style={{ borderRadius: '10px', fontWeight: '600' }}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Update Goal Progress (Section 9)">
            <Button
              type="primary"
              size="small"
              icon={<LineChartOutlined />}
              onClick={() => handleOpenProgressModal(record)}
              style={{ backgroundColor: '#13c2c2', borderColor: '#13c2c2' }}
            />
          </Tooltip>
          <Tooltip title="Edit Goal Details">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ color: '#1677ff' }} />}
              onClick={() => handleOpenModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Goal?"
            description="Are you sure you want to delete this goal?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Goal">
              <Button type="text" size="small" icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} />
      <Layout style={{ marginLeft: collapsed ? 80 : 200, height: '100vh', overflow: 'hidden', transition: 'margin-left 0.2s' }}>
        <MainHeader collapsed={collapsed} setCollapsed={setCollapsed} title="Performance Management - Goal Setting & Progress" />
        <Content style={{ margin: '24px 16px', padding: 24, background: '#f5f5f5', height: 'calc(100vh - 64px - 48px)', overflow: 'auto' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            
            {/* KPI Summary Cards */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>TOTAL GOALS ASSIGNED</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginTop: '4px' }}>{goals.length}</div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>IN PROGRESS</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#1677ff', marginTop: '4px' }}>
                    {goals.filter(g => g.status === 'In Progress').length}
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>COMPLETED / EXCEEDED</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#52c41a', marginTop: '4px' }}>
                    {goals.filter(g => ['Completed', 'Achieved', 'Exceeded'].includes(g.status)).length}
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>CRITICAL / HIGH PRIORITY</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#f5222d', marginTop: '4px' }}>
                    {goals.filter(g => ['Critical', 'High'].includes(g.priority)).length}
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Main Content Card */}
            <Card
              title={<span style={{ fontWeight: '700', fontSize: '16px', color: '#1e293b' }}>Assigned Goals Registry</span>}
              extra={
                <Space wrap>
                  <Input.Search
                    placeholder="Search Goal, Employee..."
                    style={{ width: 180 }}
                    onSearch={(val) => setSearchQuery(val)}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    allowClear
                  />
                  <Select
                    placeholder="Filter by Employee"
                    style={{ width: 180 }}
                    value={filterUser}
                    onChange={(val) => setFilterUser(val)}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {staffList.map((s) => {
                      const name = s.name || s.profile?.name || s.phone || `Staff #${s.id}`;
                      return (
                        <Option key={s.id} value={s.id}>
                          {name}
                        </Option>
                      );
                    })}
                  </Select>
                  <Select
                    placeholder="Priority"
                    style={{ width: 110 }}
                    value={filterPriority}
                    onChange={(val) => setFilterPriority(val)}
                    allowClear
                  >
                    <Option value="Critical">Critical</Option>
                    <Option value="High">High</Option>
                    <Option value="Medium">Medium</Option>
                    <Option value="Low">Low</Option>
                  </Select>
                  <Button icon={<ReloadOutlined />} onClick={fetchGoals} />
                  
                  {/* Goal Assignment Action Buttons */}
                  <Button
                    type="default"
                    icon={<TeamOutlined style={{ color: '#722ed1' }} />}
                    onClick={() => {
                      templateForm.resetFields();
                      setIsTemplateModalOpen(true);
                    }}
                  >
                    Group Auto-Assign
                  </Button>
                  <Button
                    type="default"
                    icon={<FileExcelOutlined style={{ color: '#52c41a' }} />}
                    onClick={() => setIsBulkModalOpen(true)}
                  >
                    Bulk Excel Upload
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    shape="round"
                    onClick={() => handleOpenModal()}
                  >
                    Individual Assignment
                  </Button>
                </Space>
              }
              style={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}
            >
              <Table
                columns={columns}
                dataSource={filteredGoals}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
              />
            </Card>

          </Space>
        </Content>
      </Layout>

      {/* Section 9: Goal Progress Update Modal */}
      <Modal
        title={
          <span>
            <LineChartOutlined style={{ color: '#13c2c2', marginRight: '8px' }} />
            9. Goal Progress Update
          </span>
        }
        open={isProgressModalOpen}
        onCancel={() => {
          setIsProgressModalOpen(false);
          setProgressGoal(null);
        }}
        footer={null}
        width={650}
        destroyOnClose
      >
        {progressGoal && (
          <div style={{ background: '#e6fffb', border: '1px solid #87e8de', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px' }}>
            <div style={{ fontWeight: '700', fontSize: '15px', color: '#00474f' }}>
              {progressGoal.goal}
            </div>
            <div style={{ fontSize: '12px', color: '#08979c', marginTop: '2px' }}>
              Employee: <strong>{progressGoal.employee?.profile?.name || progressGoal.employee?.name || `Staff #${progressGoal.userId}`}</strong>
              {progressGoal.target ? ` | Target: ${progressGoal.target} ${progressGoal.unit || ''}` : ''}
            </div>
          </div>
        )}

        <Form form={progressForm} layout="vertical" onFinish={handleProgressSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="target" label="Target Value">
                <Input readOnly disabled style={{ background: '#f5f5f5' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="achievement" label="Achieved Value">
                <Input
                  placeholder="e.g. 42,00,000 or 4200000"
                  onChange={(e) => {
                    const rawAchieve = parseFloat(e.target.value.replace(/,/g, '')) || 0;
                    const rawTarget = parseFloat(String(progressGoal?.target || '').replace(/,/g, '')) || 0;
                    if (rawTarget > 0 && rawAchieve >= 0) {
                      const pct = Math.min(1000, Math.round((rawAchieve / rawTarget) * 100));
                      progressForm.setFieldsValue({ achievementPct: pct });
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="achievementPct" label="Achievement %">
                <InputNumber
                  min={0}
                  max={1000}
                  formatter={value => `${value}%`}
                  parser={value => value.replace('%', '')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Progress Status"
                rules={[{ required: true, message: 'Select Progress Status' }]}
              >
                <Select placeholder="Select Progress Status">
                  <Option value="Not Started">Not Started</Option>
                  <Option value="In Progress">In Progress</Option>
                  <Option value="Completed">Completed</Option>
                  <Option value="Exceeded">Exceeded</Option>
                  <Option value="Not Achieved">Not Achieved</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="employeeComment" label="Employee Comment (Optional)">
            <Input.TextArea rows={2} placeholder="Employee progress notes, challenges, or milestones achieved..." />
          </Form.Item>

          <Form.Item name="managerComment" label="Manager Comment (Optional)">
            <Input.TextArea rows={2} placeholder="Manager review remarks, guidance, or feedback..." />
          </Form.Item>

          <Form.Item name="supportingDocument" label="Supporting Document (URL or File Link)">
            <Input placeholder="e.g. https://drive.google.com/doc/proof-123 or document filename" />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
            <Button onClick={() => setIsProgressModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<RiseOutlined />}
              style={{ backgroundColor: '#13c2c2', borderColor: '#13c2c2', fontWeight: '600' }}
            >
              Update Goal Progress
            </Button>
          </div>
        </Form>
      </Modal>

      {/* 1. Individual Assign / Edit Goal Modal - Upgraded to 880px Spacious Card Design */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: 4, height: 22, background: '#1677ff', borderRadius: 2 }} />
            <span style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
              {editingGoal ? 'Edit Assigned Goal & Progress' : 'Individual Goal Assignment'}
            </span>
          </div>
        }
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={880}
        destroyOnClose
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: '16px' }}>
          
          {/* Card 1: Employee Assignment & Goal Identity */}
          <Card
            type="inner"
            title={
              <Space>
                <UserOutlined style={{ color: '#1677ff' }} />
                <span style={{ fontWeight: 600, color: '#334155' }}>Employee Assignment & Goal Definition</span>
              </Space>
            }
            style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row gutter={16}>
              <Col span={14}>
                <Form.Item
                  name="userId"
                  label={<strong>Target Employee</strong>}
                  rules={[{ required: true, message: 'Select Employee' }]}
                >
                  <Select
                    showSearch
                    placeholder="Select Employee"
                    optionFilterProp="children"
                  >
                    {staffList.map((s) => {
                      const name = s.name || s.profile?.name || s.phone || `Staff #${s.id}`;
                      return (
                        <Option key={s.id} value={s.id}>
                          {name}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item
                  name="weightage"
                  label={<strong>Weightage (%)</strong>}
                  rules={[{ required: true, message: 'Enter Weightage %' }]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    formatter={value => `${value}%`}
                    parser={value => value.replace('%', '')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item name="priority" label={<strong>Priority</strong>} rules={[{ required: true }]}>
                  <Select>
                    <Option value="Critical">🔴 Critical</Option>
                    <Option value="High">🟠 High</Option>
                    <Option value="Medium">🔵 Medium</Option>
                    <Option value="Low">⚪ Low</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={16}>
                <Form.Item
                  name="goal"
                  label={<strong>Goal Title</strong>}
                  rules={[{ required: true, message: 'Enter Goal Title (e.g. Achieve ₹50 lakh annual sales)' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="e.g. Achieve ₹50 lakh annual sales, Launch v2 Mobile App..." />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="status" label={<strong>Goal Status</strong>} rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                  <Select>
                    <Option value="Not Started">Not Started</Option>
                    <Option value="In Progress">In Progress</Option>
                    <Option value="Completed">Completed</Option>
                    <Option value="Exceeded">Exceeded</Option>
                    <Option value="Not Achieved">Not Achieved</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Card 2: Alignment, Target Criteria & Schedule */}
          <Card
            type="inner"
            title={
              <Space>
                <AimOutlined style={{ color: '#722ed1' }} />
                <span style={{ fontWeight: 600, color: '#334155' }}>Target Criteria & PMS Alignment</span>
              </Space>
            }
            style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="kpiId" label={<strong>Linked KPI (Optional)</strong>}>
                  <Select placeholder="Select KPI (Optional)" allowClear showSearch optionFilterProp="children">
                    {kpis.map((k) => (
                      <Option key={k.id} value={k.id}>
                        {k.kpiName} ({k.kra?.kraName || 'KRA'})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="cycleId" label={<strong>Appraisal Cycle (Optional)</strong>}>
                  <Select placeholder="Select Cycle (Optional)" allowClear>
                    {cycles.map((c) => (
                      <Option key={c.id} value={c.id}>
                        {c.cycleName} (FY {c.financialYear})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="target" label={<strong>Target Value</strong>}>
                  <Input placeholder="e.g. 50,00,000, 100, 25" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="unit" label={<strong>Unit</strong>}>
                  <Input placeholder="e.g. ₹, %, Units" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="startDate" label={<strong>Start Date</strong>}>
                  <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="endDate" label={<strong>End Date</strong>}>
                  <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="description" label={<strong>Description / Guidelines</strong>} style={{ marginBottom: 0 }}>
                  <Input.TextArea rows={2} placeholder="Detailed notes, milestone expectations, evaluation rubric..." />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Card 3: Progress & Achievement Evaluation */}
          <Card
            type="inner"
            title={
              <Space>
                <LineChartOutlined style={{ color: '#13c2c2' }} />
                <span style={{ fontWeight: 600, color: '#334155' }}>Progress & Achievement Tracking</span>
              </Space>
            }
            style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="achievement" label={<strong>Achieved Value</strong>}>
                  <Input
                    placeholder="e.g. 42,00,000"
                    onChange={(e) => {
                      const rawAchieve = parseFloat(e.target.value.replace(/,/g, '')) || 0;
                      const targetStr = form.getFieldValue('target') || '';
                      const rawTarget = parseFloat(String(targetStr).replace(/,/g, '')) || 0;
                      if (rawTarget > 0 && rawAchieve >= 0) {
                        const pct = Math.min(1000, Math.round((rawAchieve / rawTarget) * 100));
                        form.setFieldsValue({ achievementPct: pct });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="achievementPct" label={<strong>Achievement %</strong>}>
                  <InputNumber
                    min={0}
                    max={1000}
                    formatter={value => `${value}%`}
                    parser={value => value.replace('%', '')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="employeeComment" label={<strong>Employee Comment</strong>}>
                  <Input.TextArea rows={2} placeholder="Employee self progress notes..." />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="managerComment" label={<strong>Manager Comment</strong>}>
                  <Input.TextArea rows={2} placeholder="Manager evaluation remarks..." />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="supportingDocument" label={<strong>Supporting Document Link</strong>} style={{ marginBottom: 0 }}>
                  <Input placeholder="e.g. https://drive.google.com/doc/proof-123 or document filename" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
            <Button size="large" onClick={handleCloseModal}>Cancel</Button>
            <Button size="large" type="primary" htmlType="submit">
              {editingGoal ? 'Update Goal & Progress' : 'Assign Goal'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* 2. Template / Group Auto-Assignment Modal - Upgraded to 880px Spacious Card Design */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: 4, height: 22, background: '#722ed1', borderRadius: 2 }} />
            <span style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
              Group / Department Auto-Assignment
            </span>
          </div>
        }
        open={isTemplateModalOpen}
        onCancel={() => setIsTemplateModalOpen(false)}
        footer={null}
        width={880}
        destroyOnClose
        style={{ top: 20 }}
      >
        <Alert
          message="Bulk-assign standardized goals to all matching employees at once based on Department, Designation, or Staff Type."
          type="info"
          showIcon
          style={{ marginBottom: '16px', borderRadius: 8 }}
        />
        <Form form={templateForm} layout="vertical" onFinish={handleTemplateSubmit}>
          
          {/* Card 1: Target Audience Scope */}
          <Card
            type="inner"
            title={
              <Space>
                <TeamOutlined style={{ color: '#722ed1' }} />
                <span style={{ fontWeight: 600, color: '#334155' }}>Target Audience & Applicability Scope</span>
              </Space>
            }
            style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="department" label={<strong>Department</strong>} style={{ marginBottom: 0 }}>
                  <Select placeholder="All Departments" allowClear showSearch>
                    {departments.map((dept) => (
                      <Option key={dept} value={dept}>
                        {dept}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="designation" label={<strong>Designation</strong>} style={{ marginBottom: 0 }}>
                  <Input placeholder="e.g. Sales Manager, Developer" allowClear />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="staffType" label={<strong>Staff Employment Type</strong>} style={{ marginBottom: 0 }}>
                  <Select placeholder="All Types" allowClear>
                    <Option value="Full-Time">Full-Time</Option>
                    <Option value="Part-Time">Part-Time</Option>
                    <Option value="Contract">Contract</Option>
                    <Option value="Intern">Intern</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Card 2: Goal Definition & Target Criteria */}
          <Card
            type="inner"
            title={
              <Space>
                <AimOutlined style={{ color: '#1677ff' }} />
                <span style={{ fontWeight: 600, color: '#334155' }}>Goal Definition & Criteria</span>
              </Space>
            }
            style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row gutter={16}>
              <Col span={16}>
                <Form.Item
                  name="goal"
                  label={<strong>Goal Title</strong>}
                  rules={[{ required: true, message: 'Goal Title is required' }]}
                >
                  <Input placeholder="e.g. Complete quarterly mandatory compliance & safety training" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="weightage"
                  label={<strong>Weightage (%)</strong>}
                  initialValue={20}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="target" label={<strong>Target Value</strong>}>
                  <Input placeholder="e.g. 100%" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="unit" label={<strong>Unit</strong>} initialValue="%">
                  <Input placeholder="e.g. %, Units, Count" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="startDate" label={<strong>Start Date</strong>}>
                  <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="endDate" label={<strong>End Date</strong>}>
                  <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="priority" label={<strong>Priority</strong>} initialValue="Medium">
                  <Select>
                    <Option value="Critical">🔴 Critical</Option>
                    <Option value="High">🟠 High</Option>
                    <Option value="Medium">🔵 Medium</Option>
                    <Option value="Low">⚪ Low</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="kpiId" label={<strong>Linked KPI (Optional)</strong>}>
                  <Select placeholder="Select KPI (Optional)" allowClear showSearch optionFilterProp="children">
                    {kpis.map((k) => (
                      <Option key={k.id} value={k.id}>
                        {k.kpiName} ({k.kra?.kraName || 'KRA'})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="cycleId" label={<strong>Appraisal Cycle (Optional)</strong>}>
                  <Select placeholder="Select Cycle (Optional)" allowClear>
                    {cycles.map((c) => (
                      <Option key={c.id} value={c.id}>
                        {c.cycleName} (FY {c.financialYear})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="description" label={<strong>Description / Guidelines</strong>} style={{ marginBottom: 0 }}>
                  <Input.TextArea rows={2} placeholder="Optional instructions or criteria for this group assignment..." />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
            <Button size="large" onClick={() => setIsTemplateModalOpen(false)}>Cancel</Button>
            <Button size="large" type="primary" htmlType="submit" style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}>
              Auto-Assign Goal to Group
            </Button>
          </div>
        </Form>
      </Modal>

      {/* 3. Bulk Excel / CSV Upload Modal */}
      <Modal
        title={
          <span style={{ fontSize: '17px', fontWeight: '700' }}>
            <FileExcelOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
            Bulk Excel / CSV Goal Upload
          </span>
        }
        open={isBulkModalOpen}
        onCancel={() => {
          setIsBulkModalOpen(false);
          setSelectedFile(null);
          setBulkRawText('');
        }}
        footer={null}
        width={720}
        destroyOnClose
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', background: '#f6ffed', border: '1px solid #b7eb8f', padding: '12px 16px', borderRadius: '10px' }}>
          <div>
            <div style={{ fontWeight: '700', color: '#135200', fontSize: '13px' }}>
              <DownloadOutlined style={{ marginRight: '6px' }} />
              Download Standard Excel Template
            </div>
            <div style={{ fontSize: '11px', color: '#389e0d' }}>
              Download the example Excel (.xlsx) file with pre-filled columns and sample data.
            </div>
          </div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', fontWeight: '600' }}
            onClick={handleDownloadSampleTemplate}
          >
            Download Excel Template (.xlsx)
          </Button>
        </div>

        <Tabs
          activeKey={activeUploadTab}
          onChange={setActiveUploadTab}
          items={[
            {
              key: 'excel',
              label: (
                <span>
                  <FileExcelOutlined style={{ color: '#52c41a' }} /> Upload Excel File (.xlsx / .csv)
                </span>
              ),
              children: (
                <div>
                  <Upload.Dragger
                    accept=".xlsx,.xls,.csv"
                    maxCount={1}
                    beforeUpload={(file) => {
                      setSelectedFile(file);
                      message.success(`${file.name} selected successfully!`);
                      return false; // Prevent automatic upload
                    }}
                    onRemove={() => setSelectedFile(null)}
                    fileList={selectedFile ? [selectedFile] : []}
                    style={{ padding: '20px', background: '#fafafa', borderRadius: '12px', border: '2px dashed #b7eb8f' }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined style={{ color: '#52c41a', fontSize: '42px' }} />
                    </p>
                    <p className="ant-upload-text" style={{ fontWeight: '600', fontSize: '15px' }}>
                      Click or drag Excel file (.xlsx / .csv) to this area to upload
                    </p>
                    <p className="ant-upload-hint" style={{ fontSize: '12px', color: '#64748b' }}>
                      Support for single .xlsx, .xls, or .csv file containing Goal & Staff ID details.
                    </p>
                  </Upload.Dragger>
                </div>
              ),
            },
            {
              key: 'paste',
              label: (
                <span>
                  <FileTextOutlined style={{ color: '#1677ff' }} /> Paste Raw CSV Text
                </span>
              ),
              children: (
                <div>
                  <Paragraph style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>
                    <strong>Format required (one row per goal):</strong><br />
                    <code>Staff Code/Phone, Goal Title, Weightage %, Target, Unit, Linked KPI, Appraisal Cycle, Start Date, End Date, Priority, Goal Status, Description</code>
                  </Paragraph>
                  <Input.TextArea
                    rows={6}
                    placeholder={`STF001, Achieve ₹50 Lakh Sales, 30, 5000000, ₹, Annual Sales KPI, Annual FY26-27, 2026-04-01, 2027-03-31, High, In Progress, Q1 Target\nSTF002, Complete 12 Demos, 20, 12, Demos, Client KPI, Mid-Year 2026, 2026-04-01, 2026-09-30, Medium, Not Started, H1 Target`}
                    value={bulkRawText}
                    onChange={(e) => setBulkRawText(e.target.value)}
                  />
                  <div style={{ marginTop: '8px' }}>
                    <Button
                      size="small"
                      icon={<FileTextOutlined />}
                      onClick={() => {
                        const sample = `Staff Code/Phone, Goal Title, Weightage %, Target, Unit, Linked KPI, Appraisal Cycle, Start Date, End Date, Priority, Goal Status, Description\n` +
                          (staffList[0]?.phone || '9876543210') + `, Achieve Sales Target, 30, 1000000, ₹, Sales Revenue KPI, Annual FY26-27, 2026-04-01, 2027-03-31, High, In Progress, Q1-Q4 Target\n` +
                          (staffList[1]?.phone || '9876543211') + `, Complete Certifications, 20, 2, Exams, Training KPI, H1 Appraisal 2026, 2026-04-01, 2026-12-31, Medium, Not Started, Exam Goal`;
                        setBulkRawText(sample);
                      }}
                    >
                      Insert Sample Text
                    </Button>
                  </div>
                </div>
              ),
            },
          ]}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
          <Button onClick={() => setIsBulkModalOpen(false)}>Cancel</Button>
          <Button
            type="primary"
            loading={bulkLoading}
            onClick={handleBulkSubmit}
            icon={<UploadOutlined />}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', fontWeight: '600' }}
          >
            Upload & Assign Goals
          </Button>
        </div>
      </Modal>
    </Layout>
  );
}
